import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { usePathLync } from "@/pathlync/PathLyncAuth";
import { ptApi, ptError, imageUrl, reportPdfUrl } from "@/pathlync/api";
import { PathLyncShell, StatusPill } from "@/pathlync/PathLyncShell";
import { ImageAnnotator } from "@/components/pathlync/ImageAnnotator";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Upload, Send, Trash2, FileText, CheckCircle2, Stethoscope,
} from "lucide-react";

const Field = ({ label, value }) => (
  value ? (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-0.5 text-sm text-slate-900">{value}</p>
    </div>
  ) : null
);

export default function ConsultDetail() {
  const { id } = useParams();
  const { user, checking } = usePathLync();
  const fileRef = useRef(null);
  const [consult, setConsult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("Microscopic");
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState({ microscopic: "", diagnosis: "", comment: "", recommendations: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await ptApi.get(`/consults/${id}`);
      setConsult(data);
    } catch (e) { toast.error(ptError(e)); } finally { setLoading(false); }
  }, [id]);

  // Wait for the session check: a hard refresh can resolve the consult before /auth/me.
  useEffect(() => { if (!checking && user) load(); }, [checking, user, load]);

  const act = async (fn, okMessage) => {
    setBusy(true);
    try {
      const { data } = await fn();
      setConsult(data);
      if (okMessage) toast.success(okMessage);
    } catch (e) { toast.error(ptError(e)); } finally { setBusy(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    const body = new FormData();
    body.append("file", file);
    try {
      await ptApi.post(`/consults/${id}/images`, body,
                       { params: { kind, caption }, timeout: 300000 });
      setCaption("");
      toast.success(`${file.name} attached`);
      await load();
    } catch (err) { toast.error(ptError(err)); } finally { setUploading(false); }
  };

  const removeImage = async (imageId) => {
    if (!window.confirm("Remove this image from the consultation?")) return;
    try {
      await ptApi.delete(`/images/${imageId}`);
      toast.success("Image removed");
      await load();
    } catch (e) { toast.error(ptError(e)); }
  };

  const sendReport = async (e) => {
    e.preventDefault();
    await act(() => ptApi.post(`/consults/${id}/report`, report),
              "Consultation report signed and sent to the laboratory");
  };

  if (checking || loading || !user) {
    return <PathLyncShell><Loader2 className="animate-spin text-slate-400" size={22} /></PathLyncShell>;
  }
  if (!consult) {
    return (
      <PathLyncShell>
        <p className="border border-dashed border-slate-300 p-12 text-center text-sm text-slate-500"
           data-testid="consult-not-found">
          This consultation is not available to your account.
        </p>
      </PathLyncShell>
    );
  }

  const isLab = user.role === "lab_user";
  const canAccept = !isLab && consult.status === "Submitted" && !consult.pathologist_name;
  const reportReady = consult.status === "Reported" && consult.report;

  return (
    <PathLyncShell>
      <Link to="/pathlync/dashboard" data-testid="back-to-dashboard"
            className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-[#1e3a8a]">
        <ArrowLeft size={15} /> Back to dashboard
      </Link>

      <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="consult-detail">
        <h1 className="font-display text-2xl font-bold tracking-tight">{consult.reference}</h1>
        <StatusPill status={consult.status} />
        {consult.priority === "Urgent" && (
          <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-800">
            Urgent
          </span>
        )}
        <div className="ml-auto flex flex-wrap gap-2">
          {canAccept && (
            <Button onClick={() => act(() => ptApi.post(`/consults/${id}/accept`),
                                       "Consultation accepted — you can annotate the images now")}
                    disabled={busy} data-testid="accept-consult-button"
                    className="bg-[#1e3a8a] hover:bg-[#172f6e]">
              <Stethoscope size={16} className="mr-2" /> Accept for review
            </Button>
          )}
          {isLab && consult.can_edit && (
            <Button onClick={() => act(() => ptApi.post(`/consults/${id}/submit`),
                                       "Consultation submitted for review")}
                    disabled={busy} data-testid="submit-consult-button"
                    className="bg-[#1e3a8a] hover:bg-[#172f6e]">
              <Send size={16} className="mr-2" /> Submit for consultation
            </Button>
          )}
          {reportReady && (
            <a href={reportPdfUrl(id)}
               target="_blank" rel="noreferrer" data-testid="view-report-pdf"
               className="inline-flex h-10 items-center gap-2 bg-emerald-700 px-4 text-sm font-semibold text-white hover:bg-emerald-800">
              <FileText size={16} /> Open report PDF
            </a>
          )}
        </div>
      </div>

      <section className="mt-6 grid gap-5 border border-slate-200 bg-white p-5 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Patient reference" value={consult.patient_reference} />
        <Field label="Age / sex" value={consult.age_sex} />
        <Field label="Specimen" value={consult.specimen_type} />
        <Field label="Collected" value={consult.collected_on} />
        <Field label="Requesting laboratory"
               value={consult.requester_institution || consult.requester_name} />
        <Field label="Requested by" value={consult.requester_name} />
        <Field label="Consulting pathologist" value={consult.pathologist_name || "Not yet accepted"} />
        <Field label="Created" value={new Date(consult.created_at).toLocaleString()} />
      </section>

      {consult.clinical_history && (
        <section className="mt-4 border border-slate-200 bg-white p-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Clinical history</p>
          <p className="mt-2 text-sm leading-relaxed text-slate-800">{consult.clinical_history}</p>
        </section>
      )}

      <section className="mt-4 border-l-4 border-[#1e3a8a] bg-white p-5">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Question for consultation
        </p>
        <p className="mt-2 text-base font-semibold text-slate-900">{consult.question}</p>
      </section>

      {isLab && consult.can_edit && (
        <section className="mt-6 border border-dashed border-slate-300 bg-white p-5"
                 data-testid="upload-panel">
          <h2 className="text-base font-semibold md:text-lg">Attach images</h2>
          <p className="mt-1 text-sm text-slate-600">
            JPEG or PNG, up to 50 MB each — gross photographs and camera-through-microscope images.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label htmlFor="img-kind" className="mb-1 block">Image type</Label>
              <select id="img-kind" value={kind} onChange={(e) => setKind(e.target.value)}
                      data-testid="image-kind-select"
                      className="mt-1 h-10 w-40 border border-slate-300 bg-white px-3 text-sm">
                <option>Microscopic</option>
                <option>Gross</option>
              </select>
            </div>
            <div className="min-w-[14rem] flex-1">
              <Label htmlFor="img-cap" className="mb-1 block">Caption</Label>
              <Input id="img-cap" value={caption} onChange={(e) => setCaption(e.target.value)}
                     data-testid="image-caption-input" placeholder="H&E 20x, invasive front" />
            </div>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png" hidden onChange={upload}
                   data-testid="image-file-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={uploading}
                    data-testid="upload-image-button" className="bg-[#1e3a8a] hover:bg-[#172f6e]">
              {uploading ? <Loader2 size={16} className="mr-2 animate-spin" />
                : <Upload size={16} className="mr-2" />}
              {uploading ? "Uploading…" : "Choose image"}
            </Button>
          </div>
        </section>
      )}

      <section className="mt-6" data-testid="images-panel">
        <h2 className="text-base font-semibold md:text-lg">
          Images <span className="text-slate-500">({consult.images.length})</span>
        </h2>
        {consult.images.length === 0 && (
          <p className="mt-3 border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500"
             data-testid="images-empty">
            No images attached yet.
          </p>
        )}
        <div className="mt-4 space-y-8">
          {consult.images.map((img) => (
            <article key={img.id} className="border border-slate-200 bg-white p-4"
                     data-testid={`image-card-${img.id}`}>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <span className="bg-slate-900 px-2 py-0.5 text-xs font-semibold text-white">
                  {img.kind}
                </span>
                <span className="text-sm text-slate-700">{img.caption || img.filename}</span>
                {!!img.annotation_shapes?.length && (
                  <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800">
                    {img.annotation_shapes.length} annotation
                    {img.annotation_shapes.length === 1 ? "" : "s"}
                  </span>
                )}
                <div className="ml-auto flex items-center gap-3">
                  <a href={imageUrl(img.id)} target="_blank" rel="noreferrer"
                     data-testid={`image-open-${img.id}`}
                     className="text-xs font-semibold text-slate-500 hover:text-[#1e3a8a]">
                    Open full size
                  </a>
                  {isLab && consult.can_edit && (
                    <button onClick={() => removeImage(img.id)} data-testid={`image-delete-${img.id}`}
                            className="text-slate-400 hover:text-red-600">
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              </div>
              <ImageAnnotator image={img} editable={!!consult.can_review}
                              onSaved={(data) => setConsult((c) => ({
                                ...c,
                                images: c.images.map((i) => (i.id === data.id ? { ...i, ...data } : i)),
                              }))} />
            </article>
          ))}
        </div>
      </section>

      {consult.can_review && (
        <section className="mt-8 border border-slate-200 bg-white p-5" data-testid="report-form">
          <h2 className="font-display text-xl font-bold tracking-tight">Consultation report</h2>
          <p className="mt-1 text-sm text-slate-600">
            Signed as {user.name}{user.credentials ? `, ${user.credentials}` : ""}. Your saved
            annotations are printed with the report.
          </p>
          <form onSubmit={sendReport} className="mt-4 space-y-4">
            <div>
              <Label htmlFor="r-micro">Microscopic description</Label>
              <Textarea id="r-micro" rows={4} value={report.microscopic}
                        data-testid="report-microscopic-input"
                        onChange={(e) => setReport((r) => ({ ...r, microscopic: e.target.value }))} />
            </div>
            <div>
              <Label htmlFor="r-dx">Consultation diagnosis</Label>
              <Textarea id="r-dx" rows={3} required value={report.diagnosis}
                        data-testid="report-diagnosis-input"
                        onChange={(e) => setReport((r) => ({ ...r, diagnosis: e.target.value }))} />
            </div>
            <div className="grid gap-4 lg:grid-cols-2">
              <div>
                <Label htmlFor="r-com">Comment</Label>
                <Textarea id="r-com" rows={3} value={report.comment}
                          data-testid="report-comment-input"
                          onChange={(e) => setReport((r) => ({ ...r, comment: e.target.value }))} />
              </div>
              <div>
                <Label htmlFor="r-rec">Recommendations</Label>
                <Textarea id="r-rec" rows={3} value={report.recommendations}
                          data-testid="report-recommendations-input"
                          onChange={(e) => setReport((r) => ({ ...r, recommendations: e.target.value }))} />
              </div>
            </div>
            <Button type="submit" disabled={busy} data-testid="submit-report-button"
                    className="bg-emerald-700 hover:bg-emerald-800">
              <CheckCircle2 size={16} className="mr-2" /> Sign and send report
            </Button>
          </form>
        </section>
      )}

      {reportReady && (
        <section className="mt-8 border-l-4 border-emerald-600 bg-white p-5" data-testid="issued-report">
          <h2 className="font-display text-xl font-bold tracking-tight">Consultation report</h2>
          <div className="mt-4 space-y-4">
            {consult.report.microscopic && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Microscopic description
                </p>
                <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-800">
                  {consult.report.microscopic}
                </p>
              </div>
            )}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Diagnosis</p>
              <p className="mt-1 whitespace-pre-wrap text-base font-semibold text-slate-900"
                 data-testid="report-diagnosis-text">
                {consult.report.diagnosis}
              </p>
            </div>
            {consult.report.comment && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Comment</p>
                <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-800">
                  {consult.report.comment}
                </p>
              </div>
            )}
            {consult.report.recommendations && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Recommendations
                </p>
                <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-800">
                  {consult.report.recommendations}
                </p>
              </div>
            )}
            <div className="border-t border-slate-200 pt-3">
              <p className="text-sm font-semibold text-slate-900">
                {consult.report.signed_by}
                {consult.report.credentials ? `, ${consult.report.credentials}` : ""}
              </p>
              <p className="text-xs text-slate-500">
                Electronically signed {new Date(consult.report.signed_at).toLocaleString()}
              </p>
            </div>
          </div>
        </section>
      )}
    </PathLyncShell>
  );
}
